import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cargosApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const CargoForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ativo: true,
    igreja_id: user?.igreja_id || '',
  });

  useEffect(() => {
    if (isEditing) {
      fetchCargo();
    }
  }, [id]);

  const fetchCargo = async () => {
    try {
      setLoading(true);
      const response = await cargosApi.get(id);
      setFormData({
        ...response.data,
        ativo: response.data.ativo !== false,
      });
    } catch (error) {
      toast.error('Erro ao carregar cargo');
      navigate('/cargos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!formData.igreja_id) {
      toast.error('Igreja é obrigatória');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await cargosApi.update(id, formData);
        toast.success('Cargo atualizado com sucesso!');
      } else {
        await cargosApi.create(formData);
        toast.success('Cargo cadastrado com sucesso!');
      }
      navigate('/cargos');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cargo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cargo-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/cargos')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="page-title">{isEditing ? 'Editar Cargo' : 'Novo Cargo'}</h1>
          <p className="page-subtitle">
            {isEditing ? 'Atualize os dados do cargo' : 'Preencha os dados do novo cargo'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="stat-card">
          <CardHeader>
            <CardTitle className="card-title">Informações do Cargo</CardTitle>
            <CardDescription>Dados básicos do cargo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="nome">Nome do Cargo *</Label>
                <Input
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="input-field mt-1"
                  placeholder="Ex: Pastor, Diácono, Presbítero"
                  data-testid="input-nome"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  className="input-field mt-1"
                  placeholder="Descrição das responsabilidades do cargo"
                  rows={4}
                  data-testid="input-descricao"
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="ativo"
                  checked={formData.ativo}
                  onCheckedChange={(c) => handleCheckboxChange('ativo', c)}
                  data-testid="checkbox-ativo"
                />
                <Label htmlFor="ativo">Cargo Ativo</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cargos')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="btn-primary"
            disabled={loading}
            data-testid="submit-cargo"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditing ? 'Atualizar' : 'Cadastrar'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CargoForm;
